// kid-carnival — b04 Karnaval. Maximalist circus birthday for a 7-year-old.
//
// "Maximalist" here means visual magic + calming sound — NOT text-heavy
// information sections. The hero balloons, falling confetti, rainbow
// headline, and rope-bordered photo are preserved intact. Bespoke text
// blocks (Sirk dasturi, Yosh poshalonchilar) have been stripped.
//
// Sound: ambient music-box pad via shared `useAmbient` hook, toggled by a
// floating bottom-right button. Two `useSound` triggers only: chime on
// RSVP yes, swoosh once on hero reveal.

import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import {
  CountdownSection,
  StorySection,
  GallerySection,
  ScheduleSection,
  MapSection,
  RsvpSection,
  ShareSection,
  GuestFooter,
} from "@/components/sections";
import { useSound } from "@/lib/sound";
import type { VariantProps } from "./types";
import { useAmbient, useReducedMotion } from "./_ambient";
import { MusicToggle } from "@/components/MusicToggle";

const RAINBOW = ["#E84A5F", "#F6B93B", "#3FA9F5", "#5BC85B", "#9B59B6", "#F58A3C"];

// ─── Deterministic RNG so SSR/client agree ────────────────────────────
function rng(seed: number): number {
  const x = Math.sin(seed * 9301 + 49297) * 233280;
  return x - Math.floor(x);
}

// ─── Floating balloons (signature) ───────────────────────────────────
interface FloatBalloon {
  id: number;
  left: number; // %
  size: number; // px
  delay: number; // s
  duration: number; // s
  drift: number; // px lateral wander
  color: string;
  rot: number;
}

function buildBalloons(n: number): FloatBalloon[] {
  return Array.from({ length: n }).map((_, i) => ({
    id: i,
    left: rng(i + 1) * 100,
    size: 36 + rng(i + 100) * 28,
    delay: -rng(i + 200) * 12,
    duration: 6 + rng(i + 300) * 6,
    drift: (rng(i + 400) - 0.5) * 80,
    color: RAINBOW[i % RAINBOW.length],
    rot: (rng(i + 500) - 0.5) * 24,
  }));
}

// ─── Falling confetti pieces ─────────────────────────────────────────
interface ConfettiPiece {
  id: number;
  left: number;
  size: number;
  delay: number;
  duration: number;
  color: string;
  shape: "rect" | "circle" | "tri";
  rot: number;
}

function buildConfetti(n: number): ConfettiPiece[] {
  const shapes: Array<ConfettiPiece["shape"]> = ["rect", "circle", "tri"];
  return Array.from({ length: n }).map((_, i) => ({
    id: i,
    left: rng(i + 11) * 100,
    size: 6 + rng(i + 22) * 8,
    delay: -rng(i + 33) * 10,
    duration: 7 + rng(i + 44) * 6,
    color: RAINBOW[i % RAINBOW.length],
    shape: shapes[i % 3],
    rot: rng(i + 55) * 360,
  }));
}

// ─── Single SVG balloon ───────────────────────────────────────────────
function BalloonSVG({ color, size, rot }: { color: string; size: number; rot: number }) {
  return (
    <svg
      viewBox="0 0 60 90"
      width={size}
      height={size * 1.5}
      style={{ display: "block", transform: `rotate(${rot}deg)`, transformOrigin: "center" }}
    >
      <ellipse cx="30" cy="32" rx="22" ry="28" fill={color} />
      <ellipse cx="22" cy="22" rx="6" ry="8" fill="#FFFFFF" opacity="0.45" />
      <path d="M 27 60 L 30 64 L 33 60 Z" fill={color} />
      <path d="M 30 64 Q 24 74 30 80 Q 36 86 30 90" stroke="#1F2330" strokeWidth="0.8" fill="none" opacity="0.7" />
    </svg>
  );
}

// ─── Big-top tent arch banner ────────────────────────────────────────
function TentArch({ accent, ink }: { accent: string; ink: string }) {
  return (
    <svg viewBox="0 0 400 80" preserveAspectRatio="none" style={{ width: "100%", height: 80, display: "block" }}>
      <defs>
        <clipPath id="b04-arch-clip">
          <path d="M 0 0 L 400 0 L 400 60 Q 200 92 0 60 Z" />
        </clipPath>
      </defs>
      <g clipPath="url(#b04-arch-clip)">
        {Array.from({ length: 22 }).map((_, i) => {
          const a1 = (-90 + (i * 180) / 22) * (Math.PI / 180);
          const a2 = (-90 + ((i + 1) * 180) / 22) * (Math.PI / 180);
          const r = 480;
          const x1 = 200 + Math.cos(a1) * r;
          const y1 = 4 + Math.sin(a1) * r;
          const x2 = 200 + Math.cos(a2) * r;
          const y2 = 4 + Math.sin(a2) * r;
          const fill = i % 2 === 0 ? accent : "#FFFFFF";
          return <path key={i} d={`M 200 4 L ${x1} ${y1} L ${x2} ${y2} Z`} fill={fill} opacity={i % 2 === 0 ? 0.95 : 0.7} />;
        })}
      </g>
      {/* Scallop edge */}
      <path
        d="M 0 60 Q 50 80 100 62 Q 150 82 200 64 Q 250 82 300 62 Q 350 80 400 60 L 400 70 Q 350 90 300 72 Q 250 92 200 74 Q 150 92 100 72 Q 50 90 0 70 Z"
        fill={accent}
      />
      {/* Pole + flag */}
      <rect x="198" y="-2" width="4" height="14" fill={ink} />
      <path d="M 200 -2 L 220 2 L 200 6 Z" fill={accent} />
      <circle cx="200" cy="-2" r="3" fill="#F6B93B" />
    </svg>
  );
}

// ─── Scroll-reveal fade-up hook ──────────────────────────────────────
function useScrollReveal() {
  useEffect(() => {
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") return;
    const els = Array.from(document.querySelectorAll<HTMLElement>(".b04-reveal"));
    const obs = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) {
            (e.target as HTMLElement).classList.add("b04-reveal-on");
            obs.unobserve(e.target);
          }
        }
      },
      { threshold: 0.12 },
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);
}

// ─── Variant ─────────────────────────────────────────────────────────
export function KidCarnivalVariant({ t, isDark, ctx }: VariantProps) {
  const { play } = useSound();
  const reduce = useReducedMotion();
  const photos = t.photos || [];

  const balloonsRef = useRef<FloatBalloon[]>(buildBalloons(12));
  const confettiRef = useRef<ConfettiPiece[]>(buildConfetti(36));
  const balloons = balloonsRef.current;
  const confetti = confettiRef.current;

  // Ambient soundscape (cheerful music-box C major up).
  const [music, setMusic] = useState(false);
  useAmbient(music, {
    freqs: [261.63, 329.63, 392.0, 523.25],
    type: "sine",
    master: 0.035,
    lfo: 0.18,
  });

  useScrollReveal();

  // Single hero swoosh on mount.
  useEffect(() => {
    if (reduce) return;
    const id = window.setTimeout(() => play("swoosh"), 600);
    return () => window.clearTimeout(id);
  }, [reduce, play]);

  // RSVP wrapper — chime on yes only.
  const wrappedSetRsvp = (v: string | null) => {
    if (v === "yes") play("chime");
    ctx.rsvp.setRsvp(v);
  };

  return (
    <div
      className="b04-kid-carnival"
      style={{
        background: t.bg,
        color: t.ink,
      }}
    >
      <style>{`
        @keyframes b04-balloon-rise {
          0%   { transform: translate3d(0, 10vh, 0) rotate(0deg); opacity: 0; }
          10%  { opacity: 0.95; }
          90%  { opacity: 0.95; }
          100% { transform: translate3d(var(--b04-bx, 0px), -110vh, 0) rotate(var(--b04-br, 0deg)); opacity: 0; }
        }
        @keyframes b04-confetti-fall {
          0%   { transform: translate3d(0, -10vh, 0) rotate(0deg); opacity: 0; }
          10%  { opacity: 1; }
          90%  { opacity: 1; }
          100% { transform: translate3d(var(--b04-cx, 0px), 110vh, 0) rotate(var(--b04-cr, 360deg)); opacity: 0; }
        }
        @keyframes b04-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        @keyframes b04-rainbow-shift {
          0% { background-position: 0% 50%; }
          100% { background-position: 200% 50%; }
        }
        @keyframes b04-rsvp-glow {
          0%, 100% { box-shadow: 0 0 0 0 ${t.accent}55, 0 0 18px 4px ${t.accent}33; }
          50%      { box-shadow: 0 0 0 8px ${t.accent}00, 0 0 28px 10px ${t.accent}66; }
        }
        .b04-rainbow-text {
          background: linear-gradient(90deg, #E84A5F, #F6B93B, #5BC85B, #3FA9F5, #9B59B6, #F58A3C, #E84A5F);
          background-size: 200% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          -webkit-text-fill-color: transparent;
          animation: b04-rainbow-shift 8s linear infinite;
        }
        .b04-balloon-hover { transition: transform 200ms ease; cursor: pointer; }
        .b04-balloon-hover:hover { transform: scale(1.08) rotate(-4deg); }
        .b04-reveal { opacity: 0; transform: translateY(24px); transition: opacity 700ms ease, transform 700ms ease; }
        .b04-reveal-on { opacity: 1; transform: translateY(0); }
        .b04-kid-carnival [class*="schedule"] li,
        .b04-kid-carnival [class*="schedule"] tr,
        .b04-kid-carnival [class*="schedule"] [role="row"] {
          transition: transform 220ms ease, background 220ms ease;
        }
        .b04-kid-carnival [class*="schedule"] li:hover,
        .b04-kid-carnival [class*="schedule"] tr:hover,
        .b04-kid-carnival [class*="schedule"] [role="row"]:hover {
          transform: scale(1.015);
        }
        .b04-kid-carnival button[data-rsvp="yes"],
        .b04-kid-carnival button[aria-pressed="true"][data-value="yes"] {
          animation: b04-rsvp-glow 2.4s ease-in-out infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .b04-kid-carnival .b04-balloon { animation: none !important; opacity: 0.95 !important; transform: none !important; }
          .b04-kid-carnival .b04-confetti { display: none !important; }
          .b04-kid-carnival .b04-bounce { animation: none !important; }
          .b04-rainbow-text { animation: none !important; }
          .b04-reveal { opacity: 1 !important; transform: none !important; transition: none !important; }
          .b04-kid-carnival button[data-rsvp="yes"],
          .b04-kid-carnival button[aria-pressed="true"][data-value="yes"] { animation: none !important; }
        }
        @media (max-width: 768px) {
          /* On mobile the hero loses the 32px side padding — the tent arch
             negative-margin breakout must be reset to match the new padding */
          .b04-kid-carnival .b04-hero { padding: 0 18px 80px !important; }
          .b04-kid-carnival .b04-tent-arch { margin-left: -18px !important; margin-right: -18px !important; }
          .b04-kid-carnival .b04-headline { font-size: clamp(56px, 14vw, 96px) !important; }
          .b04-kid-carnival .b04-countdown-row { flex-wrap: wrap !important; }
          .b04-kid-carnival .b04-balloon { animation: none !important; opacity: 0.85 !important; transform: none !important; }
          .b04-kid-carnival .b04-confetti { display: none !important; }
          .b04-kid-carnival .b04-section { padding: 64px 18px !important; }
          .b04-kid-carnival .b04-grid { grid-template-columns: 1fr !important; gap: 24px !important; }
          .b04-kid-carnival button,
          .b04-kid-carnival a[role="button"] { min-height: 44px; }
          /* Date/venue: allow wrapping */
          .b04-kid-carnival .b04-datevenue {
            font-size: 15px !important;
            white-space: normal !important;
            word-break: break-word !important;
          }
          /* Banner below photo: allow text wrap, remove rotate on mobile */
          .b04-kid-carnival .b04-banner {
            transform: none !important;
            font-size: clamp(16px, 4.5vw, 24px) !important;
            padding: 12px 20px !important;
          }
        }
        .b04-kid-carnival button:focus-visible,
        .b04-kid-carnival a:focus-visible {
          outline: 2px solid ${t.accent};
          outline-offset: 3px;
        }
      `}</style>

      {/* ═══════════════════════════════════════════════════════════════
          HERO — tent arch, rainbow headline, photo, banner.
          Floating balloons rise + confetti rains across the entire site.
          ═══════════════════════════════════════════════════════════════ */}

      {/* Site-wide balloon rise layer (fixed, behind everything) */}
      <div
        aria-hidden
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          overflow: "hidden",
        }}
      >
        {balloons.map((b) => (
          <span
            key={b.id}
            className="b04-balloon"
            style={{
              position: "absolute",
              left: `${b.left}%`,
              bottom: -120,
              opacity: reduce ? 0.85 : 0,
              animation: reduce ? "none" : `b04-balloon-rise ${b.duration}s linear ${b.delay}s infinite`,
              willChange: "transform, opacity",
              ["--b04-bx" as string]: `${b.drift}px`,
              ["--b04-br" as string]: `${b.rot}deg`,
            } as CSSProperties}
          >
            <BalloonSVG color={b.color} size={b.size} rot={b.rot} />
          </span>
        ))}
        {/* Falling confetti cascade */}
        {!reduce &&
          confetti.map((c) => (
            <span
              key={c.id}
              className="b04-confetti"
              style={{
                position: "absolute",
                left: `${c.left}%`,
                top: -40,
                width: c.size,
                height: c.size,
                opacity: 0,
                animation: `b04-confetti-fall ${c.duration}s linear ${c.delay}s infinite`,
                willChange: "transform, opacity",
                ["--b04-cx" as string]: `${(rng(c.id + 70) - 0.5) * 200}px`,
                ["--b04-cr" as string]: `${c.rot + 540}deg`,
              } as CSSProperties}
            >
              {c.shape === "rect" && (
                <span style={{ display: "block", width: "100%", height: "60%", background: c.color }} />
              )}
              {c.shape === "circle" && (
                <span style={{ display: "block", width: "100%", height: "100%", borderRadius: "50%", background: c.color }} />
              )}
              {c.shape === "tri" && (
                <svg viewBox="0 0 10 10" width="100%" height="100%">
                  <path d="M 5 0 L 10 10 L 0 10 Z" fill={c.color} />
                </svg>
              )}
            </span>
          ))}
      </div>

      <section
        className="b04-hero"
        style={{
          position: "relative",
          minHeight: "100vh",
          padding: "0 32px 80px",
          textAlign: "center",
          overflow: "hidden",
          zIndex: 1,
        }}
      >
        {/* Tent arch banner */}
        <div className="b04-tent-arch" style={{ marginLeft: -32, marginRight: -32 }}>
          <TentArch accent={t.accent} ink={t.ink} />
        </div>

        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: "0.42em",
            color: t.accent,
            marginTop: 36,
            marginBottom: 8,
          }}
        >
          MIRONBEK CIRCUS · 2026
        </div>

        {/* Giant rainbow headline */}
        <h1
          className="b04-headline b04-bounce"
          style={{
            fontFamily: t.font,
            fontWeight: 400,
            fontSize: "clamp(72px, 14vw, 168px)",
            lineHeight: 0.95,
            margin: "10px 0 4px",
            letterSpacing: "-0.01em",
            animation: "b04-bounce 3.6s ease-in-out infinite",
          }}
        >
          <span className="b04-rainbow-text">MIRONBEK 7</span>
        </h1>

        <div
          className="b04-datevenue"
          style={{
            fontFamily: t.font,
            fontStyle: "italic",
            fontSize: "clamp(20px, 3vw, 30px)",
            color: t.ink,
            marginBottom: 36,
          }}
        >
          {t.date} · {t.venue}
        </div>

        {/* Circular photo + star burst */}
        <div
          className="b04-balloon-hover"
          style={{
            position: "relative",
            width: "min(320px, 70vw)",
            aspectRatio: "1",
            margin: "0 auto",
          }}
        >
          {/* Star burst */}
          <svg
            viewBox="-100 -100 200 200"
            style={{ position: "absolute", inset: -40, width: "calc(100% + 80px)", height: "calc(100% + 80px)" }}
            aria-hidden
          >
            {Array.from({ length: 16 }).map((_, i) => {
              const a = (i * (360 / 16) * Math.PI) / 180;
              const r1 = 92;
              const r2 = 118;
              return (
                <line
                  key={i}
                  x1={Math.cos(a) * r1}
                  y1={Math.sin(a) * r1}
                  x2={Math.cos(a) * r2}
                  y2={Math.sin(a) * r2}
                  stroke="#F6B93B"
                  strokeWidth="3"
                  opacity="0.9"
                />
              );
            })}
          </svg>
          {/* Rope ring */}
          <svg viewBox="-100 -100 200 200" style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} aria-hidden>
            {Array.from({ length: 36 }).map((_, i) => {
              const a = (i * (360 / 36) * Math.PI) / 180;
              const r = 92;
              return (
                <circle
                  key={i}
                  cx={Math.cos(a) * r}
                  cy={Math.sin(a) * r}
                  r="6"
                  fill={i % 2 === 0 ? t.accent : "#F6B93B"}
                  stroke={t.ink}
                  strokeWidth="0.6"
                />
              );
            })}
          </svg>
          {/* Photo */}
          <div
            style={{
              position: "absolute",
              inset: 24,
              borderRadius: "50%",
              overflow: "hidden",
              border: `4px solid ${t.bg}`,
              boxShadow: `0 12px 40px ${t.ink}33`,
              backgroundImage: photos[0] ? `url("${photos[0]}")` : undefined,
              backgroundSize: "cover",
              backgroundPosition: "center",
              background: photos[0] ? undefined : `radial-gradient(circle, #F6B93B 0%, ${t.accent} 100%)`,
              display: "grid",
              placeItems: "center",
              color: "#FFFFFF",
              fontFamily: t.font,
              fontSize: 96,
            }}
          >
            {!photos[0] && "M"}
          </div>
        </div>

        {/* Banner below */}
        <div
          className="b04-banner"
          style={{
            display: "inline-block",
            marginTop: 40,
            padding: "16px 36px",
            background: t.accent,
            color: t.ink,
            fontFamily: t.font,
            fontWeight: 700,
            fontSize: "clamp(20px, 3.2vw, 32px)",
            transform: "rotate(-2deg)",
            border: `3px dashed ${t.ink}`,
            outline: `3px solid ${t.accent}`,
            boxShadow: `4px 4px 0 ${t.ink}`,
          }}
        >
          TUG'ILGAN KUNINGIZ MUBORAK!
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════
          COUNTDOWN
          ═══════════════════════════════════════════════════════════════ */}
      <div className="b04-reveal">
        <CountdownSection t={t} {...ctx.countdown} isDark={isDark} />
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          STORY — trimmed to 2-3 sentences
          ═══════════════════════════════════════════════════════════════ */}
      <div className="b04-reveal">
        <StorySection
          t={t}
          eyebrow="Mironbek haqida"
          title="7 yoshlik super-qahramon!"
          body="Mironbek dinozavrlarni juda yaxshi ko'radi va har kuni do'stlari bilan futbol o'ynaydi. Bu yili tug'ilgan kuni uchun u eng yaqin do'stlarini taklif qilmoqchi. Kelinglar, birgalikda bayram qilamiz!"
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          GALLERY
          ═══════════════════════════════════════════════════════════════ */}
      <div className="b04-reveal">
        <GallerySection t={t} />
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          SCHEDULE
          ═══════════════════════════════════════════════════════════════ */}
      <div className="b04-reveal">
        <ScheduleSection
          t={t}
          items={[
            ["13:30", "Mehmonlar yig'iladi"],
            ["14:00", "Sirk shousi boshlanadi!"],
            ["14:30", "O'yinlar va jumboqlar"],
            ["15:30", "Mazali piknik vaqti"],
            ["16:00", "Tort va sham!"],
            ["16:30", "Sovg'alar ochiladi"],
            ["17:00", "Raqs va musiqa"],
            ["18:00", "Vaqt tugadi — xayr!"],
          ]}
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          MAP
          ═══════════════════════════════════════════════════════════════ */}
      <div className="b04-reveal">
        <MapSection t={t} isDark={isDark} addressLine="Magic City, Tashkent" />
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          RSVP
          ═══════════════════════════════════════════════════════════════ */}
      <div className="b04-reveal">
        <RsvpSection
          t={t}
          isDark={isDark}
          rsvp={ctx.rsvp.rsvp}
          setRsvp={wrappedSetRsvp}
          guests={ctx.rsvp.guests}
          setGuests={ctx.rsvp.setGuests}
          guestName={ctx.rsvp.guestName}
          setGuestName={ctx.rsvp.setGuestName}
          label="Sirkga keling!"
          slug={ctx.slug}
        />
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          SHARE
          ═══════════════════════════════════════════════════════════════ */}
      <div className="b04-reveal">
        <ShareSection t={t} />
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          FOOTER
          ═══════════════════════════════════════════════════════════════ */}
      <GuestFooter t={t} />

      {/* Floating ambient music toggle */}
      <MusicToggle
        on={music}
        onToggle={() => setMusic((v) => !v)}
        accent={t.accent}
        ink={t.bg}
        style={{ bottom: 18, right: 18 }}
        className="b04-music-toggle"
      />
    </div>
  );
}
